import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** Variantes de badge du design system Unipros, telles qu'utilisées par la maquette. */
export type BadgeVariant =
  | 'success'
  | 'success light'
  | 'blue light'
  | 'info light'
  | 'warning'
  | 'warning light'
  | 'danger'
  | 'danger light'
  | 'neutral'
  | 'neutral border';

/**
 * Recette unique, mesurée AA (audit du 10.08.2026) : l'état est porté par la bordure
 * et la pastille — colorées, seuil 3:1 des composants d'interface — et le texte reste
 * en encre (≥ 16:1 sur tous les fonds subtils). Un texte teinté ne tenait le contraste
 * sur aucun fond subtil des rampes jade et topaz (2,69:1 et 3,41:1 au pas le plus
 * sombre) ; plutôt que deux badges lisibles sur quatre, la vocabulaire est le même
 * partout. Les variantes pleines (`success`, `warning`, `danger`) sont repliées sur
 * leur version claire pour la même raison : blanc sur jade-600 = 2,83:1.
 */
const STYLES: Record<BadgeVariant, { bg: string; fg: string; border: string; dot: string }> = {
  success: { bg: 'var(--surface-success-subtle)', fg: 'var(--text-primary)', border: 'var(--border-success-2)', dot: 'var(--status-success-strongest)' },
  'success light': { bg: 'var(--surface-success-subtle)', fg: 'var(--text-primary)', border: 'var(--border-success-2)', dot: 'var(--status-success-strongest)' },
  'blue light': { bg: 'var(--surface-brand-subtle)', fg: 'var(--text-primary)', border: 'var(--border-focus)', dot: 'var(--action-primary-default)' },
  'info light': { bg: 'var(--app-uni-it-surface-subtle)', fg: 'var(--text-primary)', border: 'var(--app-uni-it-border)', dot: 'var(--app-uni-it-action-default)' },
  warning: { bg: 'var(--surface-warning-subtle)', fg: 'var(--text-primary)', border: 'var(--border-warning)', dot: 'var(--status-warning-strongest)' },
  'warning light': { bg: 'var(--surface-warning-subtle)', fg: 'var(--text-primary)', border: 'var(--border-warning)', dot: 'var(--status-warning-strongest)' },
  danger: { bg: 'var(--surface-danger-subtle)', fg: 'var(--text-primary)', border: 'var(--border-danger)', dot: 'var(--status-danger-active)' },
  'danger light': { bg: 'var(--surface-danger-subtle)', fg: 'var(--text-primary)', border: 'var(--border-danger)', dot: 'var(--status-danger-active)' },
  neutral: { bg: 'var(--surface-subtle)', fg: 'var(--text-primary)', border: 'transparent', dot: 'var(--text-tertiary-2)' },
  'neutral border': { bg: 'transparent', fg: 'var(--text-primary)', border: 'var(--border-default)', dot: 'var(--text-tertiary-2)' },
};

@Component({
  selector: 'uniit-status-badge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      class="badge"
      [style.background]="style().bg"
      [style.color]="style().fg"
      [style.border-color]="style().border"
    >
      @if (showDot()) {
        <span class="dot" [style.background]="dotColor() || style().dot"></span>
      }
      {{ label() }}
    </span>
  `,
  styles: `
    .badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 2px 10px;
      border-radius: var(--radius-full);
      border: 1px solid transparent;
      font-family: var(--uniit-font-ui);
      font-size: 12px;
      font-weight: 500;
      white-space: nowrap;
    }

    .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      flex-shrink: 0;
    }
  `,
})
export class StatusBadgeComponent {
  readonly label = input.required<string>();
  readonly variant = input<BadgeVariant>('neutral');
  readonly showDot = input(false);
  readonly dotColor = input<string | null>(null);

  protected readonly style = computed(() => STYLES[this.variant()] ?? STYLES.neutral);
}
